import { KeyRound, LoaderCircle, Mail, Phone, Save, X } from 'lucide-react'
import { FormEvent, useEffect, useState } from 'react'
import type { NotificationPreferences, OperatorProfile } from '../types'

type Props = {
  profile: OperatorProfile | null
  busy: boolean
  onClose: () => void
  onSave: (input: { displayName: string; phone?: string; notificationPreferences: NotificationPreferences }) => Promise<void>
  onSetPassword: (password: string) => Promise<void>
}

const defaultPreferences: NotificationPreferences = { in_app: true, email: false, sms: false }

export function ProfileDrawer({ profile, busy, onClose, onSave, onSetPassword }: Props) {
  const [displayName, setDisplayName] = useState('')
  const [phone, setPhone] = useState('')
  const [preferences, setPreferences] = useState<NotificationPreferences>(defaultPreferences)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [passwordMessage, setPasswordMessage] = useState('')

  useEffect(() => {
    if (!profile) return
    setDisplayName(profile.display_name)
    setPhone(profile.phone ?? '')
    setPreferences({ ...defaultPreferences, ...profile.notification_preferences })
  }, [profile])

  async function submitProfile(event: FormEvent) {
    event.preventDefault()
    await onSave({ displayName, phone, notificationPreferences: preferences })
  }

  async function submitPassword(event: FormEvent) {
    event.preventDefault()
    setPasswordMessage('')
    if (password.length < 8) {
      setPasswordMessage('Utilisez au moins 8 caractères.')
      return
    }
    if (password !== passwordConfirmation) {
      setPasswordMessage('Les deux mots de passe ne correspondent pas.')
      return
    }
    try {
      await onSetPassword(password)
      setPassword('')
      setPasswordConfirmation('')
      setPasswordOpen(false)
    } catch (error) {
      setPasswordMessage(error instanceof Error ? error.message : 'Le mot de passe n’a pas pu être enregistré.')
    }
  }

  function toggle(key: keyof NotificationPreferences) {
    setPreferences((current) => ({ ...current, [key]: !current[key] }))
  }

  return <div className="drawer-backdrop profile-backdrop" role="presentation" onMouseDown={onClose}>
    <aside className="profile-drawer" role="dialog" aria-modal="true" aria-labelledby="profile-title" onMouseDown={(event) => event.stopPropagation()}>
      <header>
        <div><span className="eyebrow">MON PROFIL</span><h2 id="profile-title">Mon compte</h2></div>
        <button className="icon-button" onClick={onClose} aria-label="Fermer" type="button"><X size={19} /></button>
      </header>

      {!profile ? <div className="drawer-note">Chargement du profil…</div> : <>
        <form className="profile-form" onSubmit={(event) => void submitProfile(event)}>
          <label>
            Nom affiché
            <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} minLength={2} maxLength={80} required />
          </label>
          <label>
            Adresse email
            <span className="profile-readonly"><Mail size={17} /> {profile.email}</span>
          </label>
          <label>
            Téléphone <small>facultatif</small>
            <span className="input-wrap"><Phone size={17} /><input type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} maxLength={32} placeholder="Ex. +225 …" /></span>
          </label>

          <fieldset className="profile-reminders">
            <legend>Rappels</legend>
            <label className="profile-toggle"><input type="checkbox" checked={preferences.in_app} onChange={() => toggle('in_app')} /> Dans l’application</label>
          </fieldset>
          <button className="primary-button full" disabled={busy} type="submit">{busy ? <LoaderCircle className="spin" size={18} /> : <Save size={18} />} Enregistrer</button>
        </form>

        <section className="profile-password">
          <div><strong>Mot de passe</strong><span>Facultatif : le lien reçu par email reste le moyen le plus simple de se connecter.</span></div>
          {!passwordOpen ? <button className="secondary-button" onClick={() => setPasswordOpen(true)} type="button"><KeyRound size={17} /> Définir un mot de passe</button> : <form onSubmit={(event) => void submitPassword(event)}>
            <label>Nouveau mot de passe<input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="new-password" required /></label>
            <label>Confirmer le mot de passe<input type="password" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} autoComplete="new-password" required /></label>
            {passwordMessage ? <p className="profile-password-message" role="alert">{passwordMessage}</p> : null}
            <div><button className="text-button" onClick={() => { setPasswordOpen(false); setPasswordMessage('') }} type="button">Annuler</button><button className="secondary-button" disabled={busy} type="submit">Enregistrer</button></div>
          </form>}
        </section>
      </>}
    </aside>
  </div>
}
